/**
 * Cálculos de apresentação da página Meu perfil. Funções puras: recebem o dado
 * que a API já entrega e devolvem o que a tela desenha (rótulos, larguras,
 * agrupamentos). Nenhuma regra de negócio nova mora aqui.
 */
import { getLevelTitle, levelType } from 'services/utils';

/** Rótulo de cada comportamento: fonte única para o Resumo, as Dimensões e os selos. `resolutividade` é o Protagonismo. */
export const ROTULO_DO_COMPORTAMENTO: Record<Comportamento, string> = {
  colaboracao: 'Colaboração',
  foco: 'Foco no cliente',
  inovacao: 'Inovação',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança integradora',
};

const NIVEIS_DAS_BARRAS: { nivel: levelType; rotulo: string }[] = [
  { nivel: 'joga', rotulo: '#JogandoJunto' },
  { nivel: 'valeu', rotulo: '#Valeu' },
  { nivel: 'mandou', rotulo: '#MandouBem' },
  { nivel: 'uau', rotulo: '#Uau' },
];

export function rotuloNivel(nivel?: string): string {
  if (!nivel) return '';

  return `#${getLevelTitle(nivel as levelType).replace(/\s+/g, '')}`;
}

/** Barra cheia para todo nível até o atual, na mesma regra de `getLevelBarStyle`. */
export function barrasDeNivel(nivel?: string) {
  const indiceAtual = NIVEIS_DAS_BARRAS.findIndex(barra => barra.nivel === nivel);

  return NIVEIS_DAS_BARRAS.map((barra, indice) => ({ rotulo: barra.rotulo, preenchida: indice <= indiceAtual }));
}

export function progressoDoCiclo(inicio: number, fim: number, agora: number): number {
  if (fim <= inicio) return agora >= fim ? 100 : 0;

  const fracao = ((agora - inicio) / (fim - inicio)) * 100;

  return Math.min(100, Math.max(0, fracao));
}

export function larguraRelativa(valor: number, maior: number): number {
  if (!maior || maior <= 0) return 0;

  return Math.round((valor / maior) * 100);
}

export function maiorBonificacao(itens: { total?: number }[]): number {
  return itens.reduce((maior, item) => Math.max(maior, item.total ?? 0), 0);
}

interface ItemComPontos {
  total?: number;
  time?: number;
}

export function pontosPorFase<T extends ItemComPontos>(itens: T[], faseDe: (item: T) => string) {
  const fases = new Map<string, { pontos: number; inicio: number }>();

  itens.forEach(item => {
    const fase = faseDe(item);
    const atual = fases.get(fase) ?? { pontos: 0, inicio: Infinity };

    fases.set(fase, { pontos: atual.pontos + (item.total ?? 0), inicio: Math.min(atual.inicio, item.time ?? Infinity) });
  });

  const ordenadas = Array.from(fases, ([fase, { pontos, inicio }]) => ({ fase, pontos, inicio })).sort(
    (a, b) => a.inicio - b.inicio,
  );
  const maior = Math.max(0, ...ordenadas.map(fase => fase.pontos));

  return ordenadas.map(({ fase, pontos }) => ({
    fase,
    pontos,
    altura: larguraRelativa(pontos, maior),
    destaque: maior > 0 && pontos === maior,
  }));
}

export interface ItemDeSelo {
  behavior?: string;
  type?: string;
  cycle?: string;
}

/** Nome do arquivo do selo de cada comportamento; o rótulo vem de ROTULO_DO_COMPORTAMENTO. */
const SELO_POR_COMPORTAMENTO: Record<string, string> = {
  colaboracao: 'Colaboracao',
  foco: 'Foco',
  inovacao: 'Inovacao',
  resolutividade: 'Resolutividade',
  lideranca: 'Lideranca',
};

/**
 * Traduz um item de `get_selos` para o ícone `<Selo>` e os textos do cartão.
 * Mesma regra do `destaque_cultura_icon` do PointItem: comportamento vira selo
 * do Destaque da Cultura; sem comportamento, `type` indica Realização e a
 * ausência dos dois, Galeria do Reconhecimento.
 */
export function descreverSelo(item: ItemDeSelo) {
  const selo = item.behavior ? SELO_POR_COMPORTAMENTO[item.behavior] : undefined;

  if (selo) {
    return { selo, nome: 'Destaque da Cultura', categoria: ROTULO_DO_COMPORTAMENTO[item.behavior as Comportamento], ciclo: item.cycle };
  }

  if (item.type) return { selo: 'Realizacao', nome: 'Realização Destaque', categoria: 'Realizações', ciclo: item.cycle };

  return { selo: 'Galeria', nome: 'Galeria do Reconhecimento', categoria: 'Reconhecimentos', ciclo: item.cycle };
}

export type SubAbaReconhecimentos = 'RECEIVED' | 'SENDED';

/** `?active=N&sub=S`: links externos abrem uma aba (e a sub-aba de Reconhecimentos). */
export function lerAbaDaUrl(search: string): { aba?: number; sub?: SubAbaReconhecimentos } {
  const parametros = new URLSearchParams(search);
  const active = parametros.get('active');
  if (!active) return {};

  const sub = parametros.get('sub') as SubAbaReconhecimentos | null;

  return sub ? { aba: Number(active), sub } : { aba: Number(active) };
}

export type Comportamento = 'colaboracao' | 'foco' | 'resolutividade' | 'lideranca' | 'inovacao';
export type PontosPorComportamento = Record<Comportamento, number>;

/** AJH-11: o total da coleção, de todos os ciclos ("16 selos"). */
export function rotuloSelos(quantidade: number): string {
  return quantidade === 1 ? '1 selo' : `${quantidade} selos`;
}

/** Ordem do bloco Reconhecimentos do Resumo (a das Dimensões do Comportamento é outra, como no design). */
const ORDEM_DO_RESUMO: Comportamento[] = ['colaboracao', 'foco', 'resolutividade', 'lideranca', 'inovacao'];

export const COMPORTAMENTOS: { chave: Comportamento; rotulo: string }[] = ORDEM_DO_RESUMO.map(chave => ({
  chave,
  rotulo: ROTULO_DO_COMPORTAMENTO[chave],
}));

/**
 * Liderança Integradora só pontua para quem lidera (REC-24). Para funcionário fica fora da lista,
 * a não ser que ele já tenha ponto nela no ciclo (foi gestor antes), para não esconder dado real.
 */
export function comportamentosVisiveis<T extends { chave: Comportamento }>(lista: T[], ehGestor: boolean, pontos: PontosPorComportamento): T[] {
  return ehGestor || pontos.lideranca > 0 ? lista : lista.filter(item => item.chave !== 'lideranca');
}

/** `getKPIPointsOnline` devolve `positions: [{ behavior, total }]`; comportamento ausente vale 0. */
export function lerPontosPorComportamento(resultado?: { positions?: { behavior: string; total?: number }[] }) {
  const pontos: PontosPorComportamento = { colaboracao: 0, foco: 0, resolutividade: 0, lideranca: 0, inovacao: 0 };

  resultado?.positions?.forEach(posicao => {
    if (posicao.behavior in pontos) pontos[posicao.behavior as Comportamento] = posicao.total ?? 0;
  });

  return pontos;
}

/** URL da foto quando a pessoa escolheu imagem (`avatar_type` 1); com avatar, não há foto. */
export function urlDaFoto(avatar: { avatar_type?: number; image?: unknown }): string | undefined {
  const imagem = avatar.image as { medium?: { url?: string }; small?: { url?: string } } | undefined;

  return avatar.avatar_type === 1 ? imagem?.medium?.url || imagem?.small?.url : undefined;
}

/**
 * Ciclo aberto: o de `status` "open" em `task_cycle__c`, o mesmo critério do `currentCycle`. Decide o bloco
 * Realizações Destaque do Resumo (AJ2-34).
 */
export function cicloEstaAberto(ciclo?: { status?: string }): boolean {
  return ciclo?.status === 'open';
}
